import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  CampaignActivityView,
  ChangeLogView,
  ParameterView,
  UpdateParameterInput,
} from '@/api/parameters-client';
import { useParametersClient } from '@/api/parameters-client-context';

// =============================================================================
// use-parameters.ts — Hooks TanStack Query de l'écran A-24 (via le double).
// =============================================================================
// Lecture (liste, changelog) en useQuery ; édition (PATCH) en useMutation. Le
// client effectif vient du ParametersClientProvider (doublable). Aucune règle
// métier ici : la mutation relaie la valeur au client, l'erreur API remonte
// telle quelle (l'autorité de validation est l'API, [S2-TRANCHÉ #4]).

const PARAMETERS_KEY = ['app-parameters'] as const;

/** GET liste — tous les paramètres applicatifs. */
export function useParametersList(): UseQueryResult<ParameterView[], Error> {
  const client = useParametersClient();
  return useQuery<ParameterView[], Error>({
    queryKey: PARAMETERS_KEY,
    queryFn: () => client.list(),
  });
}

/** GET changelog — historique read-only d'un paramètre (activé si paramKey fourni). */
export function useParameterChangelog(
  paramKey: string | null,
): UseQueryResult<ChangeLogView[], Error> {
  const client = useParametersClient();
  return useQuery<ChangeLogView[], Error>({
    queryKey: [...PARAMETERS_KEY, 'changelog', paramKey],
    queryFn: () => client.changelog(paramKey as string),
    enabled: paramKey !== null,
  });
}

/**
 * GET signal d'activité campagne — bandeau d'impact CONDITIONNEL de l'écran A-24
 * (V28-IMPACT-BANNER, DBU §2.8 l.1854). `degraded: true` (FAIL-CLOSED INVERSÉ, côté API) ⇒ le
 * bandeau doit être AFFICHÉ malgré tout (voir parameter-edit-form.tsx) — ce hook relaie la valeur
 * telle quelle, il ne réinterprète rien.
 */
export function useCampaignActivity(): UseQueryResult<CampaignActivityView, Error> {
  const client = useParametersClient();
  return useQuery<CampaignActivityView, Error>({
    queryKey: [...PARAMETERS_KEY, 'campaign-activity'],
    queryFn: () => client.getCampaignActivity(),
  });
}

/** Variables de la mutation d'édition (PATCH). */
export interface UpdateParameterVars {
  readonly paramKey: string;
  readonly input: UpdateParameterInput;
}

/**
 * PATCH édition. En cas de succès, invalide la liste ET le changelog du paramètre
 * (refetch). L'erreur (valid_range, type) remonte via `mutation.error` pour
 * affichage : l'UI ne réinterprète pas la règle, elle relaie le message serveur.
 */
export function useUpdateParameter(): UseMutationResult<
  ParameterView,
  Error,
  UpdateParameterVars
> {
  const client = useParametersClient();
  const queryClient = useQueryClient();
  return useMutation<ParameterView, Error, UpdateParameterVars>({
    mutationKey: [...PARAMETERS_KEY, 'update'],
    mutationFn: ({ paramKey, input }: UpdateParameterVars) =>
      client.update(paramKey, input),
    onSuccess: (_data, { paramKey }) => {
      void queryClient.invalidateQueries({ queryKey: PARAMETERS_KEY });
      void queryClient.invalidateQueries({
        queryKey: [...PARAMETERS_KEY, 'changelog', paramKey],
      });
    },
  });
}
